import { useState } from 'react'
import BigButton from './BigButton'
import { api } from '../lib/api'
import type { MyFeedback } from '../lib/types'

const TERUG_OPTIES = ['Ja', 'Misschien', 'Neen'] as const
const SCORE_LABELS = ['Slecht', 'Matig', 'Oké', 'Goed', 'Top'] as const

// Straten/plekken waar standhouders graag een volgende rommelmarkt bij zouden
// zien — meerkeuze. Zelfde lijst als server/routes/me.ts; wijzig ze samen.
const STRATEN_OPTIES = [
  'Werkstraat',
  'Virginiastraat',
  'Tijkstraat/Textielstraat',
  'Sisalstraat',
  'Scharlakenstraat/Drapierstraat',
  'Meer plekjes aan de voetpaden in het park',
] as const

// Kort feedbackformulier na afloop van de markt. Drie vragen, twee ervan met
// grote knoppen — tikken op een telefoon moet genoeg zijn, typen is optioneel.
export default function FeedbackForm({
  token,
  initial,
}: {
  token: string
  initial: MyFeedback | null
}) {
  const [score, setScore] = useState<number | null>(initial?.score ?? null)
  const [terug, setTerug] = useState<string | null>(initial?.terug ?? null)
  const [tekst, setTekst] = useState(initial?.tekst ?? '')
  const [straten, setStraten] = useState<string[]>(initial?.straten ?? [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(initial?.ontvangen === true)
  const [changing, setChanging] = useState(false)
  // Snapshot of the last successfully saved answer, so "Annuleer" can put the
  // form fields back to it. Without this, canceling out of an edit left the
  // just-abandoned (unsaved) edits sitting in state — reopening "Iets
  // aanpassen" afterwards showed those instead of what's actually in Notion,
  // risking an accidental resubmit of values the standhouder already rejected.
  const [savedAnswer, setSavedAnswer] = useState({
    score: initial?.score ?? null,
    terug: initial?.terug ?? null,
    tekst: initial?.tekst ?? '',
    straten: initial?.straten ?? [],
  })

  const leeg = score === null && terug === null && tekst.trim() === '' && straten.length === 0

  function toggleStraat(optie: string) {
    setStraten((cur) => (cur.includes(optie) ? cur.filter((s) => s !== optie) : [...cur, optie]))
  }

  async function submit() {
    setBusy(true)
    setError(null)
    const res = await api<{ feedback: MyFeedback }>(
      `/api/me/${encodeURIComponent(token)}/feedback`,
      { method: 'POST', body: JSON.stringify({ score, terug, tekst, straten }) },
    )
    setBusy(false)
    if (!res.ok) {
      setError(
        res.error === 'empty'
          ? 'Vul minstens één ding in.'
          : 'Er ging iets mis. Probeer het zo opnieuw.',
      )
      return
    }
    setSaved(true)
    setChanging(false)
    setSavedAnswer({ score, terug, tekst, straten })
  }

  if (saved && !changing) {
    return (
      <div className="card p-5 shadow-block bg-lime/40">
        <p className="font-display text-3xl tracking-display uppercase mb-1">Bedankt!</p>
        <p className="text-base">
          We hebben je antwoord goed ontvangen. Het helpt ons om de volgende editie beter te maken.
        </p>
        <button
          type="button"
          onClick={() => { setChanging(true); setSaved(false) }}
          className="text-sm text-muted underline mt-3 hover:text-ink"
        >
          Iets aanpassen
        </button>
      </div>
    )
  }

  return (
    <div className="card p-5 shadow-block">
      <p className="font-display text-3xl tracking-display uppercase mb-1">Hoe was het voor jou?</p>
      <p className="text-base text-muted mb-4">
        Vier korte vragen — je hoeft niets te typen als je niet wil.
      </p>

      <fieldset className="mb-5">
        <legend className="font-display text-xl tracking-display uppercase mb-2">
          1. Hoe tevreden ben je over deze rommelmarkt?
        </legend>
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={score === n}
              onClick={() => setScore(score === n ? null : n)}
              className={`px-4 py-3 min-w-[4.5rem] border-[3px] border-ink font-display tracking-display uppercase text-lg shadow-block-sm hover:translate-y-[1px] ${
                score === n ? 'bg-ink text-lime' : 'bg-offwhite'
              }`}
            >
              <span className="block text-2xl leading-none">{n}</span>
              <span className="block text-xs mt-1">{SCORE_LABELS[n - 1]}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-5">
        <legend className="font-display text-xl tracking-display uppercase mb-2">
          2. Doe je volgende editie weer mee?
        </legend>
        <div className="flex flex-wrap gap-2">
          {TERUG_OPTIES.map((opt) => (
            <button
              key={opt}
              type="button"
              aria-pressed={terug === opt}
              onClick={() => setTerug(terug === opt ? null : opt)}
              className={`px-5 py-3 border-[3px] border-ink font-display tracking-display uppercase text-lg shadow-block-sm hover:translate-y-[1px] ${
                terug === opt ? 'bg-ink text-lime' : 'bg-offwhite'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-5">
        <legend className="font-display text-xl tracking-display uppercase mb-2">
          3. Wat mag volgend jaar beter?
        </legend>
        <textarea
          className="input min-h-[7rem]"
          value={tekst}
          onChange={(e) => setTekst(e.target.value)}
          maxLength={2000}
          placeholder="Bv. de uren, de plekken, de opbouw, de communicatie… (mag leeg blijven)"
        />
      </fieldset>

      <fieldset className="mb-5">
        <legend className="font-display text-xl tracking-display uppercase mb-2">
          4. In welke straten zou je ook graag rommelmarkt zien? (kies er zoveel je wil)
        </legend>
        <div className="flex flex-wrap gap-2">
          {STRATEN_OPTIES.map((optie) => (
            <button
              key={optie}
              type="button"
              aria-pressed={straten.includes(optie)}
              onClick={() => toggleStraat(optie)}
              className={`px-4 py-3 border-[3px] border-ink font-display tracking-display uppercase text-base shadow-block-sm hover:translate-y-[1px] ${
                straten.includes(optie) ? 'bg-ink text-lime' : 'bg-offwhite'
              }`}
            >
              {optie}
            </button>
          ))}
        </div>
      </fieldset>

      {error && (
        <p
          role="alert"
          className="mb-4 border-[3px] border-ink bg-red text-offwhite px-4 py-3 font-display tracking-display uppercase text-sm"
        >
          {error}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <BigButton onClick={submit} disabled={busy || leeg}>
          {busy ? 'Bezig…' : 'Verstuur mijn feedback'}
        </BigButton>
        {changing && (
          <button
            type="button"
            onClick={() => {
              setScore(savedAnswer.score)
              setTerug(savedAnswer.terug)
              setTekst(savedAnswer.tekst)
              setStraten(savedAnswer.straten)
              setChanging(false)
              setSaved(true)
            }}
            className="text-sm text-muted underline hover:text-ink"
          >
            Annuleer
          </button>
        )}
      </div>
    </div>
  )
}
